import type { InsertApprovalMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertApprovalDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { InsertApprovalMutationVariables } from '../nativeMutationInputs';
import { mapInsertApprovalVariables } from './approvalInputMapping';

export const useInsertApproval = createMutationHook({
  name: 'insertApproval',
  mutationOptions: (trpc, onSuccess) =>
    trpc.frontend.approval.insert.mutationOptions({ onSuccess }),
  mapInput: (approval: InsertApprovalMutationVariables['approval']) =>
    mapInsertApprovalVariables(approval),
  invalidate: (trpc) => [
    trpc.frontend.approval.globalApprovals.queryKey(),
    trpc.frontend.approval.approvalLevels.queryKey(),
    // COI-889: `ApprovalModal` builds its whole update payload from the fetched
    // approval, so a stale `approvalById` is read back into the next save.
    trpc.frontend.approval.approvalById.queryKey(),
  ],
  // Maps the tRPC mutation response to match the GraphQL mutation structure
  // so consumers can branch on success without knowing which path ran.
  mapResponse: (result: { Id: string }): InsertApprovalMutation => ({
    insert_approval_one: {
      __typename: 'approval',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertApprovalDocument,
  graphqlVariables: (
    approval: InsertApprovalMutationVariables['approval']
  ) => ({ approval }),
  // `getGlobalApprovals` reads the `approval` root field and `getApprovalLevels`
  // reads `approval_level`; evict both so the Apollo (flag-off) path refreshes
  // both feeds (mirrors the tRPC branch's `invalidate`).
  evictFields: ['approval', 'approval_level'],
});
